import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import api from '@/api/client'
import { useAuthStore } from '@/stores/authStore'
import { BarChart3, Plus, Vote } from 'lucide-react'

interface PollOption { text: string; votes: number; votedBy?: string[] }
interface Poll { _id: string; question: string; options: PollOption[]; totalVotes: number; username: string; expiresAt?: string }

export default function Polls() {
  const { isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState(['', ''])
  const [showCreate, setShowCreate] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['polls'],
    queryFn: () => api.get('/polls').then((response) => response.data),
  })

  const createPoll = useMutation({
    mutationFn: () => api.post('/polls', { question, options: options.filter(Boolean) }),
    onSuccess: () => {
      setQuestion('')
      setOptions(['', ''])
      setShowCreate(false)
      queryClient.invalidateQueries({ queryKey: ['polls'] })
    },
  })

  const vote = useMutation({
    mutationFn: ({ pollId, optionIndex }: { pollId: string; optionIndex: number }) => api.post(`/polls/${pollId}/vote`, { optionIndex }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['polls'] }),
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-primary" /> Anketler</h1>
          <p className="text-sm text-muted-foreground mt-1">Topluluğun fikrini keşfet.</p>
        </div>
        {isAuthenticated && <button className="inline-flex items-center gap-2 rounded-lg bg-primary text-white px-4 py-2" onClick={() => setShowCreate((value) => !value)}><Plus className="w-4 h-4" /> Anket oluştur</button>}
      </div>

      {showCreate && (
        <form className="rounded-xl border border-border bg-card p-4 space-y-3" onSubmit={(event) => { event.preventDefault(); createPoll.mutate() }}>
          <input className="w-full rounded-lg border border-border bg-secondary px-3 py-2" value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="Sorunuzu yazın" maxLength={500} required />
          {options.map((option, index) => <input key={index} className="w-full rounded-lg border border-border bg-secondary px-3 py-2" value={option} onChange={(event) => setOptions((current) => current.map((item, itemIndex) => itemIndex === index ? event.target.value : item))} placeholder={`Seçenek ${index + 1}`} maxLength={200} required />)}
          <button type="button" className="text-sm text-primary" onClick={() => setOptions((current) => [...current, ''])}>+ Seçenek ekle</button>
          <button disabled={createPoll.isPending} className="block rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50">{createPoll.isPending ? 'Gönderiliyor...' : 'Yayınla'}</button>
        </form>
      )}

      {isLoading && <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">Anketler yükleniyor...</div>}
      {isError && <div className="rounded-xl border border-destructive/40 bg-card p-6 text-destructive">Anketler yüklenemedi.</div>}
      {!isAuthenticated && <p className="text-sm text-muted-foreground">Oy vermek için <Link className="text-primary underline" to="/login">giriş yapın</Link>.</p>}

      {(data?.polls || []).map((poll: Poll) => (
        <article key={poll._id} className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div><p className="font-semibold">{poll.question}</p><p className="text-xs text-muted-foreground mt-1">@{poll.username} · {poll.totalVotes} oy</p></div>
          <div className="space-y-2">{poll.options.map((option, index) => {
            const percentage = poll.totalVotes ? Math.round((option.votes / poll.totalVotes) * 100) : 0
            return <button key={index} disabled={!isAuthenticated || vote.isPending} onClick={() => vote.mutate({ pollId: poll._id, optionIndex: index })} className="relative block w-full overflow-hidden rounded-lg border border-border px-3 py-3 text-left disabled:cursor-not-allowed disabled:opacity-70"><span className="absolute inset-y-0 left-0 bg-primary/10" style={{ width: `${percentage}%` }} /><span className="relative flex items-center justify-between gap-3"><span>{option.text}</span><span className="text-xs text-muted-foreground">{percentage}%</span></span></button>
          })}</div>
          {vote.isError && <p className="text-sm text-destructive">Oy kullanılamadı veya bu seçenekte zaten oy kullandınız.</p>}
        </article>
      ))}
      {!isLoading && !data?.polls?.length && <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground"><Vote className="mx-auto mb-2" />Henüz anket yok.</div>}
    </div>
  )
}
